import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { formatPrice } from "../../../utility/formatters";
import { ArrowDownIcon, ErrorIcon, SuccessIcon } from "../../Icons/Icons";
import { OperatorBox } from "./ArcCoOperatorBox";
import ArcCoPaymentSummaryBox from "./ArcCoPaymentSummaryBox";
import { NoDataAlert } from "./NoDataAlert";
import PlcContent from "./PlcContent";

export const PlcWorksheet = ({ plcCalculation, marketYearAveragePriceStatus = null }) => {
  const renderContent = () => {
    if (!plcCalculation) {
      return <NoDataAlert message="No PLC calculation data available" />;
    }

    const { inputs, outputs } = plcCalculation;
    const effectivePrice = Math.max(
      inputs.national_loan_rate ?? 0,
      inputs.market_year_average_price ?? inputs.effective_price ?? 0
    );
    const isPaymentTriggered = outputs.plcPaymentPrice > 0;
    const paymentStatus = isPaymentTriggered
      ? { icon: <SuccessIcon color="currentColor" />, text: "Payment Triggered" }
      : { icon: <ErrorIcon color="currentColor" />, text: "No Payment" };
    const expectedPayment = inputs.share_percent < 1 ? outputs.plcPayment : outputs.totalPlcPayment;

    const rightPanel = (
      <Stack spacing={1.5} sx={{ textAlign: "center" }} className="arc-plc-steps-content">
        <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              borderBottom: 1,
              borderColor: "colors.borderLightGray",
              padding: "14px 18px",
              textAlign: "left",
            }}
          >
            Payment Trigger
          </Typography>

          <Stack
            sx={{
              padding: { xs: "16px 12px 12px", sm: "20px 18px 16px" },
              justifyContent: "space-between",
              flexDirection: "row",
              alignItems: "stretch",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
            }}
          >
            <ArcCoPaymentSummaryBox
              title="Effective Reference Price"
              value={inputs.effective_reference_price}
              formatOptions={{ places: 2 }}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                minHeight: { xs: "80px", sm: "90px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            />
            <OperatorBox>{isPaymentTriggered ? ">" : "≤"}</OperatorBox>
            <ArcCoPaymentSummaryBox
              title="Effective Price"
              value={effectivePrice}
              formatOptions={{ places: 2 }}
              sx={{
                flex: "1 1 0",
                minWidth: 0,
                minHeight: { xs: "80px", sm: "90px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            />
          </Stack>

          <Typography variant="body1" color="text.secondary" sx={{ textTransform: "uppercase" }}>
            Payment Status
          </Typography>

          <Box
            sx={{
              backgroundColor: isPaymentTriggered ? "colors.backgroundSuccess" : "colors.backgroundWarning",
              borderRadius: "10rem",
              padding: "14px 18px",
              justifySelf: "center",
              alignItems: "center",
              marginBottom: "20px",
              marginTop: "6px",
              fontSize: "1.125rem",
              fontWeight: 600,
              display: "flex",
              border: 2,
              color: isPaymentTriggered ? "success.main" : "warning.main",
              gap: 1,
            }}
          >
            {paymentStatus.icon}
            {paymentStatus.text}
          </Box>

          {!isPaymentTriggered && (
            <Alert
              severity="info"
              sx={{
                borderRadius: "0.5rem",
                border: 1,
                margin: "0 18px 20px",
                fontWeight: 600,
                color: "info.dark",
              }}
            >
              No PLC payment is triggered because the effective reference price (
              {formatPrice(inputs.effective_reference_price, 2)}) is less than or equal to the effective price (
              {formatPrice(effectivePrice, 2)}).
            </Alert>
          )}
        </Paper>

        {isPaymentTriggered && (
          <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                borderBottom: 1,
                borderColor: "colors.borderLightGray",
                padding: "14px 18px",
                textAlign: "left",
              }}
            >
              Expected Payment
            </Typography>

            <Box
              color="success.main"
              sx={{
                backgroundColor: "colors.backgroundSuccess",
                borderRadius: "0.5rem",
                padding: "14px 18px",
                justifySelf: "center",
                border: 2,
                margin: 2.5,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                EXPECTED PAYMENT
              </Typography>

              <Typography
                sx={{
                  fontWeight: "700 !important",
                  fontSize: "1.5rem",
                  paddingTop: "0.5rem",
                  wordBreak: "break-all",
                }}
              >
                {formatPrice(expectedPayment, 0)}
              </Typography>
            </Box>
          </Paper>
        )}
      </Stack>
    );

    return (
      <Grid container spacing={2} sx={{ flexDirection: { xs: "column-reverse", lg: "row" } }}>
        <Grid item xs={12} lg={7} xl={8} className="arc-plc-steps-content">
          <PlcContent plcCalculation={plcCalculation} />
        </Grid>
        <Grid item xs={12} lg={5} xl={4} className="arc-co-right-panel">
          {rightPanel}
        </Grid>
      </Grid>
    );
  };

  return (
    <Accordion
      className="arc-plc-worksheet-accordion"
      disableGutters
      sx={{
        backgroundColor: "background.paper",
        "&.Mui-expanded": {
          backgroundColor: "grey.100",
        },
        borderRadius: "0.875rem !important",
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        sx={{
          padding: 3,
          flexDirection: "row-reverse",
          "& .MuiAccordionSummary-content": {
            margin: "0 0 0 1rem",
          },
        }}
        expandIcon={
          <ArrowDownIcon
            sx={{
              width: 38,
              height: 38,
            }}
          />
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
          <Typography
            sx={{ fontSize: "1.5rem !important", fontWeight: "700 !important", mb: "0 !important" }}
            variant="h5"
          >
            PLC: Payment Calculation Worksheet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This worksheet shows the step-by-step calculation for PLC payments.
            {marketYearAveragePriceStatus === "projected" && " (Based on projected MYA price)"}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ "&>.MuiStack-root": { gap: 1 } }}>{renderContent()}</AccordionDetails>
    </Accordion>
  );
};
